'use client';

import { useCallback, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { EATALY_FULLSCREEN_NAV_OVERLAY_ID } from './eataly-fullscreen-nav-overlay';
import { SvgClose, SvgMenu } from '../icons-eataly';

export type EatalyHeaderNavTriggerProps = {
    open: boolean;
    menuClosing: boolean;
    onOpen: () => void;
    onClose: () => void;
};

/** Menu open/close trigger — state lives in `EatalyHeader` with a single shared overlay. */
export default function EatalyHeaderNavTrigger({
    open,
    menuClosing,
    onOpen,
    onClose,
}: EatalyHeaderNavTriggerProps): ReactElement {
    const { t } = useTranslation('header');

    const handleClick = useCallback(() => {
        if (open) {
            onClose();
            return;
        }
        if (!menuClosing) {
            onOpen();
        }
    }, [open, menuClosing, onOpen, onClose]);

    return (
        <Button
            type="button"
            hierarchy="tertiary"
            iconOnly
            size="icon"
            onClick={handleClick}
            data-testid="eataly-header-menu-trigger"
            aria-label={open ? t('closeMenu') : t('openMenu')}
            aria-expanded={open}
            aria-controls={open ? EATALY_FULLSCREEN_NAV_OVERLAY_ID : undefined}
            className="shrink-0 hover:bg-transparent hover:opacity-70 cursor-pointer">
            {open ? <SvgClose className="size-6" aria-hidden /> : <SvgMenu className="size-6" aria-hidden />}
        </Button>
    );
}
